import { useEffect, useRef } from 'react';
import type { GridApiCommon, GridEventListener } from '@mui/x-data-grid';
import type { RefObject } from 'react';

/**
 * Community DataGrid has no `onRowsScrollEnd`. Subscribe to scroll position
 * and fire once whenever the viewport nears the bottom.
 */
export function useRowsScrollEnd(apiRef: RefObject<GridApiCommon | null>, onRowsScrollEnd: (() => void) | undefined, thresholdPx = 120): void {
  const firedNearBottom = useRef(false);

  useEffect(() => {
    if (!onRowsScrollEnd) return undefined;
    const api = apiRef.current;
    if (!api) return undefined;

    const handleScroll: GridEventListener<'scrollPositionChange'> = (params) => {
      const dimensions = api.getRootDimensions();
      if (!dimensions) return;

      const viewport = dimensions.viewportInnerSize.height;
      const content = dimensions.contentSize.height;
      if (content <= viewport) return;

      const remaining = content - (params.top + viewport);
      if (remaining <= thresholdPx) {
        if (!firedNearBottom.current) {
          firedNearBottom.current = true;
          onRowsScrollEnd();
        }
      } else {
        firedNearBottom.current = false;
      }
    };

    return api.subscribeEvent('scrollPositionChange', handleScroll);
  }, [apiRef, onRowsScrollEnd, thresholdPx]);
}
